<script setup>
// Astra DS · OndaCheck — el diagnóstico de la onda.
//
// Se enseña con `?onda-check=1` en cualquier página. No se pinta nunca sin ese
// parámetro, así que no estorba a nadie.
//
// 🔴 Por qué existe: la onda se mueve en Chrome de escritorio y **no** en
// Safari ni en el iPhone (reportado por Pablo el 29.08.26). Desde este lado no
// se puede abrir la consola del navegador de otro, y sin datos toca suponer,
// que es como se pierden las tardes. Esto responde en pantalla las cuatro cosas
// que hacen falta para saber DÓNDE se corta la cadena:
//
//   1. Por dónde entra el audio: `blob:` es hls.js con Media Source, `.m3u8` es
//      HLS nativo del sistema y `.mp3` es el flujo progresivo. Solo el primero
//      permite analizar.
//   2. Si el navegador tiene Web Audio y en qué estado está su contexto:
//      `suspended` significa que nadie lo ha reanudado tras el gesto.
//   3. El pico que ve el analizador ahora mismo. Cero constante con la radio
//      sonando es la firma de «el audio no pasa por el grafo».
//   4. Si el elemento está reproduciendo de verdad.
import { computed, onMounted, onUnmounted, ref } from "vue";

const props = defineProps({
  // Los niveles que ya tiene la vista, para no montar un segundo análisis.
  levels: { type: Array, default: () => [] },
  playing: { type: Boolean, default: false },
  // El estado del AudioContext. En Safari es la línea que decide: `suspended`
  // con la radio sonando significa que Web Audio nunca arrancó.
  estado: { type: String, default: "" },
  supported: { type: Boolean, default: true },
  // La decisión: `esperando`, `si` o `no`. Es lo que apaga el medidor del hero,
  // así que si aquí pone `esperando` con la radio sonando, el que no decide es
  // el composable, no el componente que pinta.
  analisis: { type: String, default: "" },
});

const visible = ref(false);
const fuente = ref("");
const reproduciendo = ref(false);
const tiempo = ref(0);
const picoMax = ref(0);
// Cuántos segundos lleva el analizador leyendo. Si esto se queda en 0 con la
// radio sonando, el cronómetro de la decisión no ha arrancado.
const segundos = ref(0);
let arranque = 0;

let reloj = null;

const soporte = {
  webAudio: !!(window.AudioContext || window.webkitAudioContext),
  mediaSource: !!window.MediaSource,
  managed: !!window.ManagedMediaSource,
};

const pico = computed(() => {
  let p = 0;
  for (const v of props.levels) if (typeof v === "number" && v > p) p = v;
  return p;
});

// Por dónde entra el audio, leído del propio elemento y no de lo que creamos
// que hace el reproductor.
function mirar() {
  const el = document.querySelector("audio");
  if (!el) {
    fuente.value = "sin elemento <audio>";
    return;
  }

  const src = el.currentSrc || el.src || "";
  if (src.startsWith("blob:")) fuente.value = "Media Source (hls.js) ✅";
  else if (src.includes(".m3u8")) fuente.value = "HLS nativo del sistema ❌";
  else if (src.includes(".mp3")) fuente.value = "MP3 progresivo";
  else fuente.value = src ? src.slice(0, 40) : "sin fuente";

  reproduciendo.value = !el.paused;
  tiempo.value = Math.round(el.currentTime);

  if (pico.value > picoMax.value) picoMax.value = pico.value;

  if (props.levels.length && !arranque) arranque = Date.now();
  segundos.value = arranque ? Math.round((Date.now() - arranque) / 1000) : 0;
}

onMounted(() => {
  visible.value = new URLSearchParams(location.search).has("onda-check");
  if (!visible.value) return;
  mirar();
  reloj = setInterval(mirar, 500);
});

onUnmounted(() => {
  if (reloj) clearInterval(reloj);
});
</script>

<template>
  <div v-if="visible" class="onda-check">
    <p class="onda-check__t">Diagnóstico de la onda</p>
    <dl>
      <dt>Audio entra por</dt>
      <dd>{{ fuente }}</dd>

      <dt>Reproduciendo</dt>
      <dd>{{ reproduciendo ? `sí · ${tiempo}s` : "no" }}</dd>

      <dt>Estado del botón</dt>
      <dd>{{ playing ? "playing" : "parado" }}</dd>

      <dt>Pico ahora</dt>
      <dd>{{ pico.toFixed(3) }}</dd>

      <dt>Pico máximo visto</dt>
      <dd :class="{ 'is-mal': picoMax === 0 }">{{ picoMax.toFixed(3) }}</dd>

      <dt>Bandas</dt>
      <dd>{{ levels.length }}</dd>

      <dt>Leyendo desde</dt>
      <dd :class="{ 'is-mal': segundos === 0 && reproduciendo }">{{ segundos }}s</dd>

      <dt>Análisis</dt>
      <dd :class="{ 'is-mal': analisis === 'esperando' && reproduciendo }">{{ analisis || "?" }}</dd>

      <dt>AudioContext</dt>
      <dd :class="{ 'is-mal': estado !== 'running' }">{{ estado || "?" }}</dd>

      <dt>Analizador</dt>
      <dd :class="{ 'is-mal': !supported }">{{ supported ? "sí" : "NO" }}</dd>

      <dt>Web Audio</dt>
      <dd>{{ soporte.webAudio ? "sí" : "NO" }}</dd>

      <dt>MediaSource</dt>
      <dd>{{ soporte.mediaSource ? "sí" : "no" }}</dd>

      <dt>ManagedMediaSource</dt>
      <dd>{{ soporte.managed ? "sí" : "no" }}</dd>
    </dl>
    <p class="onda-check__n">
      Con la radio sonando, un pico máximo de 0.000 significa que el audio no
      pasa por el grafo de Web Audio.
    </p>
  </div>
</template>

<style scoped lang="scss">
.onda-check {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 9999;
  max-width: 320px;
  padding: 12px 14px;
  background: #000;
  border: 1px solid var(--color-border-accent);
  color: #f5f3ee;
  font-family: var(--font-mono), monospace;
  font-size: 11px;
  line-height: 1.5;
}

.onda-check__t {
  margin: 0 0 8px;
  color: var(--color-text-accent);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

dl {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 2px 10px;
  margin: 0;
}

dt {
  color: #8c8880;
}

dd {
  margin: 0;
  text-align: right;
  word-break: break-all;
}

.is-mal {
  color: var(--color-text-accent);
}

.onda-check__n {
  margin: 10px 0 0;
  color: #8c8880;
}
</style>
